/**
 * TrXL Shop - Frontend Error Reporter
 * Sendet JS-Errors, unhandled promises & fehlgeschlagene API-Calls an /api/errors/log
 */

const API = process.env.REACT_APP_BACKEND_URL || '';

// Basic throttling: max 1 report per 2s for the same message
const _recent = new Map();
function shouldReport(key) {
  const now = Date.now();
  const last = _recent.get(key) || 0;
  if (now - last < 2000) return false;
  _recent.set(key, now);
  // Cleanup old keys
  if (_recent.size > 200) {
    for (const [k, t] of _recent.entries()) if (now - t > 60000) _recent.delete(k);
  }
  return true;
}

function getUserEmail() {
  try {
    const token = localStorage.getItem('trxl_token');
    if (!token) return '';
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.email || '';
  } catch { return ''; }
}

export function reportError(message, opts = {}) {
  if (!message) return;
  const msg = String(message).substring(0, 500);
  const key = msg + '|' + (opts.context || '');
  if (!shouldReport(key)) return;
  try {
    fetch(`${API}/api/errors/log`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message: msg,
        severity: opts.severity || 'error',
        context: (opts.context || window.location.pathname + window.location.search).substring(0, 500),
        stack: (opts.stack || '').substring(0, 4000),
        user_agent: navigator.userAgent,
        user_email: getUserEmail(),
        extra: opts.extra || null,
      }),
      keepalive: true,
    }).catch(() => {});
  } catch { /* silent */ }
}

let _installed = false;
export function installErrorReporter() {
  if (_installed || typeof window === 'undefined') return;
  _installed = true;

  // Track if user is leaving / page in background — fetches that fail then are NOT bugs
  let _leaving = false;
  window.addEventListener('beforeunload', () => { _leaving = true; });
  window.addEventListener('pagehide', () => { _leaving = true; });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') _leaving = true;
    else setTimeout(() => { _leaving = false; }, 1000);
  });

  // Pattern to suppress: navigation-induced fetch aborts (not real bugs)
  const NAV_FAIL_RE = /AbortError|NetworkError when attempting|Failed to fetch|Load failed|Network request failed|cancelled|The user aborted|Importing a module script failed|ChunkLoadError|Loading chunk \d+ failed/i;

  // 3rd-party SDK noise (Safari/iOS native bridge detection, ad-tracker pings, etc.)
  const THIRD_PARTY_NOISE_RE = /window\.webkit|messageHandlers|gtag is not defined|fbq is not defined|google_tag_manager|adsbygoogle|FB SDK|VK\.com|tiktok\.com|hotjar|clarity|linkedin\.com|reddit\.com|adsservice/i;

  // Uncaught JS errors
  window.addEventListener('error', (e) => {
    if (!e || !e.message) return;
    if (/ResizeObserver|Script error\.?$/i.test(e.message)) return;
    if (NAV_FAIL_RE.test(e.message)) return;
    if (THIRD_PARTY_NOISE_RE.test(e.message)) return;
    // Errors thrown from 3rd-party scripts (filename not on our origin)
    if (e.filename && !e.filename.startsWith(window.location.origin) && /^https?:/i.test(e.filename)) return;
    reportError(e.message, {
      severity: 'error',
      context: `${window.location.pathname}${e.filename ? ` @ ${e.filename}:${e.lineno}` : ''}`,
      stack: e.error && e.error.stack ? e.error.stack : '',
    });
  });

  // Unhandled promise rejections
  window.addEventListener('unhandledrejection', (e) => {
    const reason = e && e.reason;
    if (!reason) return;
    const msg = reason.message || String(reason);
    if (NAV_FAIL_RE.test(msg)) return;
    if (THIRD_PARTY_NOISE_RE.test(msg)) return;
    if (_leaving) return;
    reportError(msg, {
      severity: 'error',
      context: `unhandledrejection @ ${window.location.pathname}`,
      stack: reason.stack || '',
    });
  });

  // Patch fetch to track 5xx — but ignore navigation-induced aborts and
  // any fetches to external/3rd-party domains (we only care about our own backend).
  try {
    const origFetch = window.fetch;
    const ownOrigin = window.location.origin;
    window.fetch = async (...args) => {
      const url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || '';
      const isExternal = /^https?:\/\//i.test(url) && !url.startsWith(ownOrigin);
      try {
        const res = await origFetch(...args);
        if (res && res.status >= 500 && !isExternal && !url.includes('/api/errors/log')) {
          reportError(`HTTP ${res.status} at ${url}`, {
            severity: res.status >= 502 ? 'critical' : 'error',
            context: `fetch ${url}`,
          });
        }
        return res;
      } catch (err) {
        // Skip our own error endpoint, external resources, navigation aborts
        if (url.includes('/api/errors/log') || url.includes('/api/analytics/track')) throw err;
        if (isExternal) throw err;
        if (_leaving || document.visibilityState === 'hidden') throw err;
        const msg = err?.message || '';
        if (NAV_FAIL_RE.test(msg)) throw err;
        reportError(`Fetch fail: ${msg}`, {
          severity: 'warning',
          context: `fetch ${url}`,
          stack: err?.stack || '',
        });
        throw err;
      }
    };
  } catch { /* patch failed, skip */ }
}
